import React from "react";
import BowlerScoreRow, { BowlerScoreMobileCard } from "../BowlerScoreRow";

const TeamScores = ({
  team,
  bowlers,
  onScoreUpdate,
  canEnterScores,
  isCalculatingTotals,
  getCalculationResult,
  teamScratchTotal,
  teamHandicapTotal,
  isTeamVerified,
  verificationStatus,
  isScoreSaving,
  compact = false,
}) => {
  // Check if any scores are currently being saved for this team
  const isAnyScoreSaving = bowlers.some((bowler) => {
    const gamesPerEvent = team.gamesPerEvent || 3;
    for (let i = 0; i < gamesPerEvent; i++) {
      if (isScoreSaving(team.id, bowler.id, i)) {
        return true;
      }
    }
    return false;
  });

  // Show loading indicator if team is still loading
  if (team.isLoading) {
    return (
      <div className="px-4 py-8">
        <div className="text-center text-brand-muted">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-gold-ink"></div>
            <span>Loading team data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (bowlers.length === 0) {
    return (
      <div className="px-4 py-8">
        <div className="text-center text-brand-muted">
          No bowlers assigned to this team.
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "px-3 pt-4 pb-3" : "px-4 pt-6 pb-3"}>
      {/* Mobile Cards View */}
      <div className={compact ? "space-y-3" : "md:hidden space-y-3"}>
        {bowlers.map((bowler, index) => (
          <BowlerScoreMobileCard
            key={bowler.id}
            bowler={bowler}
            teamId={team.id}
            onScoreUpdate={onScoreUpdate}
            isEven={index % 2 === 1}
            gamesPerEvent={team.gamesPerEvent || 3}
            canEditScores={
              canEnterScores &&
              !isTeamVerified &&
              !verificationStatus?.payouts?.isVerified
            }
            calculatingTotals={isCalculatingTotals}
            getCalculationResult={getCalculationResult}
            teamTransformedScores={team.transformedScores}
            isScoreSaving={isScoreSaving}
          />
        ))}

        {/* Mobile Team Totals */}
        {bowlers.length > 0 && (
          <div className="card-inset p-4 mt-4">
            <div className="flex items-center justify-center mb-3">
              {isTeamVerified && (
                <span className="mr-2 border border-brand-charcoal/20 text-brand-charcoal px-2 py-1 rounded-lg text-xs font-display font-bold tracking-wide">
                  VERIFIED
                </span>
              )}
              <h4 className="text-lg font-display font-bold text-brand-charcoal">
                Team Totals
              </h4>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs font-display tracking-wider uppercase text-brand-muted">Scratch Total</div>
                <div className="font-score text-xl text-brand-charcoal">
                  {isCalculatingTotals || isAnyScoreSaving ? (
                    <span className="text-brand-mist">Calculating...</span>
                  ) : (
                    teamScratchTotal
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-display tracking-wider uppercase text-brand-muted">Handicap</div>
                <div className="font-score text-xl text-brand-charcoal">
                  {isCalculatingTotals || isAnyScoreSaving ? (
                    <span className="text-brand-mist">Calculating...</span>
                  ) : (
                    teamHandicapTotal - teamScratchTotal
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-display tracking-wider uppercase text-brand-muted">Handicap Total</div>
                <div className="font-score text-xl text-brand-charcoal">
                  {isCalculatingTotals || isAnyScoreSaving ? (
                    <span className="text-brand-mist">Calculating...</span>
                  ) : (
                    teamHandicapTotal
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className={compact ? "hidden" : "hidden md:block"}>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="hidden md:table-cell p-2.5 text-center bg-brand-charcoal text-brand-cream text-xs font-display font-bold tracking-wider uppercase">
                Side Pots
              </th>
              <th className="p-2.5 text-center bg-brand-charcoal text-brand-cream text-xs font-display font-bold tracking-wider uppercase">Bowler</th>
              <th className="hidden md:table-cell p-2.5 text-center bg-brand-charcoal text-brand-cream text-xs font-display font-bold tracking-wider uppercase">
                Average
              </th>
              <th className="hidden md:table-cell p-2.5 text-center bg-brand-charcoal text-brand-cream text-xs font-display font-bold tracking-wider uppercase">
                Handicap
              </th>
              {Array.from({
                length: Math.max(1, team.gamesPerEvent || 3),
              }).map((_, idx) => (
                <th
                  key={idx}
                  className="hidden md:table-cell p-2.5 text-center bg-brand-charcoal text-brand-cream text-xs font-display font-bold tracking-wider uppercase"
                >{`Game ${idx + 1}`}</th>
              ))}
              <th
                className="md:hidden p-2.5 text-center bg-brand-charcoal text-brand-cream"
                colSpan={Math.max(1, team.gamesPerEvent || 3)}
              >
                {/* Removed "Games" heading for mobile since we use cards now */}
              </th>
              <th className="hidden md:table-cell p-2.5 text-center bg-brand-charcoal text-brand-cream text-xs font-display font-bold tracking-wider uppercase">
                Scratch Total
              </th>
              <th className="hidden md:table-cell p-2.5 text-center bg-brand-charcoal text-brand-cream text-xs font-display font-bold tracking-wider uppercase">
                Handicap Total
              </th>
            </tr>
          </thead>
          <tbody>
            {bowlers.map((bowler, index) => (
              <BowlerScoreRow
                key={bowler.id}
                bowler={bowler}
                teamId={team.id}
                onScoreUpdate={onScoreUpdate}
                isEven={index % 2 === 1}
                gamesPerEvent={team.gamesPerEvent || 3}
                canEditScores={
                  canEnterScores &&
                  !isTeamVerified &&
                  !verificationStatus?.payouts?.isVerified
                }
                calculatingTotals={isCalculatingTotals}
                getCalculationResult={getCalculationResult}
                teamTransformedScores={team.transformedScores}
                isScoreSaving={isScoreSaving}
              />
            ))}

            {/* Team totals rows */}
            <tr className="font-bold bg-brand-cream m-0">
              <td
                colSpan={4 + (team.gamesPerEvent || 3)}
                className="text-right hidden md:table-cell p-0"
              >
                <span className="flex items-center justify-end pr-2">
                  {isTeamVerified && (
                    <span className="mr-2 border border-brand-charcoal/20 text-brand-charcoal px-2 py-1 rounded-lg text-xs font-display font-bold tracking-wide">
                      VERIFIED
                    </span>
                  )}
                  <span>Team Totals:</span>
                </span>
              </td>
              <td className="hidden md:table-cell p-0 text-center font-score">
                {isCalculatingTotals || isAnyScoreSaving ? (
                  <span className="text-brand-mist">Calculating...</span>
                ) : (
                  teamScratchTotal
                )}
              </td>
              <td className="hidden md:table-cell p-0 text-center font-score">
                {isCalculatingTotals || isAnyScoreSaving ? (
                  <span className="text-brand-mist">Calculating...</span>
                ) : (
                  teamHandicapTotal
                )}
              </td>
            </tr>

            {/* Additional totals rows for mobile */}
            <tr className="font-bold bg-brand-cream m-0">
              <td
                colSpan={2 + (team.gamesPerEvent || 3)}
                className="text-right md:hidden p-0"
              >
                Scratch Total:
              </td>
              <td className="md:hidden p-0 text-center font-score">
                {isCalculatingTotals || isAnyScoreSaving ? (
                  <span className="text-brand-mist">Calculating...</span>
                ) : (
                  teamScratchTotal
                )}
              </td>
            </tr>
            <tr className="font-bold bg-brand-cream m-0">
              <td
                colSpan={2 + (team.gamesPerEvent || 3)}
                className="text-right md:hidden p-0"
              >
                Handicap:
              </td>
              <td className="md:hidden p-0 text-center font-score">
                {isCalculatingTotals || isAnyScoreSaving ? (
                  <span className="text-brand-mist">Calculating...</span>
                ) : (
                  teamHandicapTotal - teamScratchTotal
                )}
              </td>
            </tr>
            <tr className="font-bold bg-brand-cream m-0">
              <td
                colSpan={2 + (team.gamesPerEvent || 3)}
                className="text-right md:hidden p-0"
              >
                Handicap Total:
              </td>
              <td className="md:hidden p-0 text-center font-score">
                {isCalculatingTotals || isAnyScoreSaving ? (
                  <span className="text-brand-mist">Calculating...</span>
                ) : (
                  teamHandicapTotal
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamScores;
